import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, ArrowRight } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import { PageMeta, SITE_URL } from "@/lib/seo";
import FounderVoiceNote from "@/components/FounderVoiceNote";
import FounderStory from "@/components/FounderStory";
import TrustpilotCollector from "@/components/TrustpilotCollector";
import ROUTE_META from "@/lib/routeMeta.json";

const PERSON_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Uncle Danny",
  jobTitle: "Founder of Umbra · certified life coach",
  image: `${SITE_URL}/about/uncle-danny-baghdad-2006.jpg`,
  url: `${SITE_URL}/about`,
  sameAs: ["https://www.instagram.com/shadowspeakjournal/"],
};

const BOOKS = [
  { title: "Coming Home, Fellas (And Everybody Else)", sub: "Ritual traditions for trauma, combat PTSD, C-PTSD, and narcissistic abuse", img: "/about/coming-home-fellas.jpg", id: "coming-home" },
  { title: "Focus, Fellas (And Everybody Else)", sub: "A field guide to actually listening in the bedroom", img: "/about/focus-fellas.jpg", id: "focus-fellas" },
];

function Bio() {
  return (
    <section className="grid grid-cols-1 md:grid-cols-[minmax(0,280px)_1fr] gap-10 md:gap-14 items-start" data-testid="about-founder">
      <figure>
        <img
          src="/about/uncle-danny-baghdad-2006.jpg"
          alt="Uncle Danny, founder of Umbra, in uniform in Baghdad in 2006"
          className="w-full border-2 border-terracotta/70"
          data-testid="about-founder-photo"
        />
        <figcaption className="mt-3 text-center font-serif italic text-terracotta">Baghdad, 2006</figcaption>
      </figure>
      <div>
        <p className="font-sans uppercase tracking-[0.32em] text-xs text-terracotta mb-5">About the founder</p>
        <p className="font-serif text-xl sm:text-2xl text-bone-100 leading-relaxed" data-testid="about-founder-bio">
          <strong className="font-semibold">Uncle Danny</strong> is an Iraq combat veteran who served in Baghdad in
          2006, a certified life coach, a stand-up comedian, and the founder of Umbra and the Shadow Speak Journal.
          After years of fighting the war at home, he found his way back through a wife who listened, veteran
          programs, and a lot of hard work. Now he directs his fire at helping others come home. He also wrote{" "}
          <em>Focus, Fellas</em> and the Umbra Library, and lives in Oklahoma with his family.
        </p>
        <FounderVoiceNote />
        <div className="mt-8 flex flex-wrap gap-5">
          <a href="https://www.instagram.com/shadowspeakjournal/" target="_blank" rel="noopener noreferrer" data-testid="about-instagram-link" className="inline-flex items-center gap-2 min-h-[44px] font-sans text-xs uppercase tracking-[0.24em] text-bone-300 hover:text-terracotta">
            <Instagram className="h-4 w-4" /> @shadowspeakjournal
          </a>
          <a href="mailto:hello@shadowspeakjournal.com" data-testid="about-email-link" className="inline-flex items-center gap-2 min-h-[44px] font-sans text-xs uppercase tracking-[0.24em] text-bone-300 hover:text-terracotta">
            <Mail className="h-4 w-4" /> hello@shadowspeakjournal.com
          </a>
        </div>
      </div>
    </section>
  );
}

function Books() {
  return (
    <section className="mt-24 border-t border-obsidian-600 pt-14" data-testid="about-books">
      <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-8">Also by Uncle Danny</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 max-w-3xl">
        {BOOKS.map((b) => (
          <div key={b.id} className="flex gap-5 items-start" data-testid={`about-book-${b.id}`}>
            <img src={b.img} alt={`Cover of ${b.title}`} className="w-28 shrink-0 border border-obsidian-600" loading="lazy" />
            <div>
              <p className="font-serif text-lg text-bone-100 leading-snug">{b.title}</p>
              <p className="mt-2 font-sans text-sm text-bone-400 leading-relaxed">{b.sub}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function About() {
  return (
    <div className="min-h-screen bg-obsidian-900">
      <PageMeta
        {...ROUTE_META["/about"]}
        path="/about"
        jsonLd={[PERSON_JSONLD]}
      />
      <SiteHeader />
      <main className="max-w-5xl mx-auto px-6 sm:px-8 md:px-16 pt-14 sm:pt-24 pb-20">
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-bone-100 max-w-3xl mb-14 sm:mb-20" data-testid="about-title">
          The light doesn't erase the shadow. <em className="text-terracotta not-italic">Someone has to listen to it.</em>
        </h1>
        <Bio />
        <FounderStory />
        <section className="mt-20 max-w-2xl">
          <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-5">Why Umbra exists</p>
          <p className="font-sans text-base sm:text-lg text-bone-300 leading-relaxed">
            Umbra is a guided shadow-work journal created to help you meet the parts of yourself you've been afraid
            to hear. You speak your answers, the journal writes them down, and a gentle Mirror reflects back what you
            said. Not therapy, and not for anyone seeking a diagnosis — one small step toward the light.
          </p>
          <Link to="/try" data-testid="about-try-cta" className="mt-8 inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-deep text-obsidian-900 px-6 min-h-[48px] font-sans text-xs uppercase tracking-[0.22em] transition-colors">
            Try Chapter One — no account <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="mt-12 border-t border-obsidian-600 pt-8">
            <p className="font-sans text-sm text-bone-400 mb-3">Been listening a while? A short review helps the next person begin.</p>
            <TrustpilotCollector id="about-trustpilot-collector" className="max-w-[240px]" />
          </div>
        </section>
        <Books />
      </main>
    </div>
  );
}
